import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';

/**
 * Hooks für den KI-Routinen-Generator (Sprint 6, WT-601..WT-606).
 *
 * Backend-Endpunkte (siehe app/api/routes/ai.py + schemas/ai.py):
 *   POST /ai/suggest-routine              -> AISuggestRoutineResponse
 *   POST /ai/validate-playbook            -> AIValidatePlaybookResponse
 *   POST /ai/suggest-routine/{id}/accept  -> AIAcceptSuggestionResponse
 *   GET  /ai/sessions                     -> AISessionListResponse
 *   GET  /ai/sessions/{id}                -> AISessionDetailResponse
 *
 * Das Backend liefert eine "wire-form" mit kurzen Feldnamen
 * (`tokens.input`, `validation.ok`). Das Frontend arbeitet intern mit einem
 * UI-freundlicheren Modell (`TokenUsage`, `ValidationResult` mit Issues +
 * Severity). Die Mapping-Helper unten halten den Drift weg.
 *
 * WICHTIG (Wire-Format): validate-playbook und accept arbeiten gegen das
 * strukturierte `playbook_json` (PlaybookProposal, Backend-Schema mit
 * extra=forbid) - NICHT gegen einen {playbook_yaml}-Wrapper. Wir reichen das
 * `playbook_json` aus der Suggest-Response durch (siehe AiSuggestResponse).
 * Echte 4xx-Fehler werden bewusst NICHT durch DEV-Mocks kaschiert.
 */

// ---------------------------------------------------------------------------
// UI-Modell (das, was die Komponenten konsumieren)
// ---------------------------------------------------------------------------

export type ValidationCheck = 'schema' | 'allowlist' | 'syntax';

export interface ValidationIssue {
  severity: 'error' | 'warning' | 'info';
  check: ValidationCheck;
  message: string;
  line?: number | null;
  hint?: string | null;
}

export interface ValidationResult {
  schema_valid: boolean;
  allowlist_valid: boolean;
  syntax_valid: boolean;
  modules_used: string[];
  modules_blocked: string[];
  issues: ValidationIssue[];
}

export interface TokenUsage {
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_creation_tokens: number;
  cost_usd: number;
}

export interface AiSuggestRequest {
  system_id?: string | null;
  customer_id?: string | null;
  intent: string;
}

export interface AiSuggestResponse {
  session_id: string;
  intent_pseudonym: string;
  /**
   * Strukturiertes PlaybookProposal-JSON (de-pseudonymisiert). Das ist die
   * Source-of-Truth für validate + accept; das YAML ist nur die kompilierte
   * Ansicht.
   */
  playbook_json: Record<string, unknown>;
  playbook_yaml: string;
  description_md: string;
  proposed_slug: string;
  proposed_display_name: string;
  is_destructive: boolean;
  requires_snapshot: boolean;
  validation: ValidationResult;
  usage: TokenUsage;
  created_at: string;
}

export interface AiValidateRequest {
  /** Strukturiertes PlaybookProposal-JSON (Backend: AIValidatePlaybookRequest). */
  playbook_json: Record<string, unknown>;
}

export interface AiValidateResponse {
  validation: ValidationResult;
}

export interface AiAcceptRequest {
  /** Strukturiertes (ggf. user-editiertes) PlaybookProposal-JSON. */
  playbook_json: Record<string, unknown>;
  /** Optionaler Routine-Code; Backend generiert sonst automatisch. */
  target_routine_code?: string | null;
}

export interface AiAcceptResponse {
  routine_id: string;
  routine_slug: string;
  redirect_to: string;
}

export interface AiSession {
  id: string;
  created_at: string;
  customer_id?: string | null;
  customer_name?: string | null;
  system_id?: string | null;
  system_pseudonym?: string | null;
  /**
   * Der echte (pseudonymisierte) Intent wird vom Listen-Endpoint NICHT
   * geliefert - er steht nur im Detail. In der Liste bleibt das Feld leer
   * (die UI zeigt dann einen Platzhalter + purpose als Kontext).
   */
  intent_truncated: string;
  /** Backend `purpose` (z.B. "routine_generation") - Kontext statt Intent. */
  purpose?: string;
  status: 'success' | 'rejected' | 'failed' | 'accepted';
  validation_summary: {
    schema_valid: boolean;
    allowlist_valid: boolean;
    syntax_valid: boolean;
  };
  usage: TokenUsage;
  accepted_routine_id?: string | null;
}

export interface AiSessionListResponse {
  data: AiSession[];
  total: number;
}

export interface AiSessionDetail extends AiSession {
  intent_full: string;
  playbook_yaml: string;
  description_md: string;
  validation: ValidationResult;
}

export interface AiCostStatus {
  date: string;
  cost_today_usd: number;
  cost_this_month_usd: number;
  daily_soft_limit_usd: number;
  daily_hard_limit_usd: number;
  soft_limit_reached: boolean;
  hard_limit_reached: boolean;
}

// ---------------------------------------------------------------------------
// Backend-Wire-Modell (raw)
// ---------------------------------------------------------------------------

interface WireValidation {
  ok: boolean;
  errors?: string[];
  warnings?: string[];
  syntax_error?: string | null;
}

interface WireTokens {
  input?: number;
  cache_creation?: number;
  cache_read?: number;
  output?: number;
}

interface WireSuggestResponse {
  ki_session_id: string;
  playbook_json: Record<string, unknown>;
  playbook_yaml?: string | null;
  validation: WireValidation;
  tokens: WireTokens;
  cost_usd: number;
  model: string;
  duration_ms: number;
  /** Optional: Backend kann diese Felder später nachreichen. */
  is_destructive?: boolean | null;
  requires_snapshot?: boolean | null;
  description_md?: string | null;
  proposed_slug?: string | null;
  proposed_display_name?: string | null;
  intent_pseudonym?: string | null;
  created_at?: string | null;
}

interface WireValidateResponse {
  validation: WireValidation;
  playbook_yaml?: string | null;
}

interface WireAcceptResponse {
  routine_id: string;
  ki_session_id: string;
  playbook_path?: string | null;
  /** Optional: Slug muss vom Backend zurückgegeben werden, fallback aus path. */
  routine_slug?: string | null;
}

interface WireSessionItem {
  id: string;
  purpose: string;
  model: string;
  started_at: string;
  duration_ms?: number | null;
  cost_usd?: number | null;
  tokens_input?: number;
  tokens_cache_read?: number;
  tokens_cache_creation?: number;
  tokens_output?: number;
  validation_passed?: boolean | null;
  routine_id?: string | null;
  customer_id?: string | null;
  system_id?: string | null;
  intent_truncated?: string | null;
}

interface WireSessionList {
  data: WireSessionItem[];
  total: number;
}

interface WireSessionDetail extends WireSessionItem {
  pseudonymized_input?: Record<string, unknown> | null;
  pseudonymized_output?: Record<string, unknown> | null;
  validation_errors?: Record<string, unknown> | null;
}

// ---------------------------------------------------------------------------
// Mapping Wire -> UI
// ---------------------------------------------------------------------------

const FORBIDDEN_MODULES_RE = /\b(shell|raw|command|script)\b/i;

function mapTokens(t: WireTokens | undefined, costUsd: number): TokenUsage {
  return {
    input_tokens: t?.input ?? 0,
    cache_creation_tokens: t?.cache_creation ?? 0,
    cache_read_tokens: t?.cache_read ?? 0,
    output_tokens: t?.output ?? 0,
    cost_usd: costUsd,
  };
}

function mapValidation(w: WireValidation): ValidationResult {
  const errors = w.errors ?? [];
  const warnings = w.warnings ?? [];
  const syntaxError = w.syntax_error ?? null;

  // Heuristik: ob Allowlist-Verletzung in errors[] steht.
  const allowlistMessages = errors.filter((m) => FORBIDDEN_MODULES_RE.test(m));
  const schemaMessages = errors.filter(
    (m) => !FORBIDDEN_MODULES_RE.test(m) && !/syntax/i.test(m)
  );
  const blockedModules = Array.from(
    new Set(
      errors
        .map((m) => m.match(FORBIDDEN_MODULES_RE)?.[0])
        .filter((x): x is string => Boolean(x))
    )
  );

  const issues: ValidationIssue[] = [];
  for (const msg of schemaMessages) {
    issues.push({ severity: 'error', check: 'schema', message: msg });
  }
  for (const msg of allowlistMessages) {
    issues.push({
      severity: 'error',
      check: 'allowlist',
      message: msg,
      hint: 'Verwende ein deklaratives Modul (apt/service/copy/template/lineinfile).',
    });
  }
  if (syntaxError) {
    issues.push({ severity: 'error', check: 'syntax', message: syntaxError });
  }
  for (const msg of warnings) {
    issues.push({ severity: 'warning', check: 'schema', message: msg });
  }

  return {
    schema_valid: schemaMessages.length === 0,
    allowlist_valid: allowlistMessages.length === 0,
    syntax_valid: !syntaxError,
    modules_used: [],
    modules_blocked: blockedModules,
    issues,
  };
}

function mapSuggestResponse(
  raw: WireSuggestResponse,
  fallback: { intent: string }
): AiSuggestResponse {
  // name / description / is_destructive / requires_snapshot stehen im
  // strukturierten PlaybookProposal (playbook_json), nicht als Top-Level-Feld
  // der Suggest-Response. Wir lesen sie defensiv dort heraus.
  const pj = (raw.playbook_json ?? {}) as Record<string, unknown>;
  const pjName = typeof pj['name'] === 'string' ? (pj['name'] as string) : null;
  const pjDesc =
    typeof pj['description'] === 'string' ? (pj['description'] as string) : null;
  const pjDestructive =
    typeof pj['is_destructive'] === 'boolean' ? (pj['is_destructive'] as boolean) : null;
  const pjSnapshot =
    typeof pj['requires_snapshot'] === 'boolean'
      ? (pj['requires_snapshot'] as boolean)
      : null;

  const playbookYaml =
    raw.playbook_yaml ?? jsonToYamlBestEffort(raw.playbook_json);
  const proposedSlug =
    raw.proposed_slug ??
    (pjName ? slugify(pjName) : null) ??
    guessSlugFromYaml(playbookYaml) ??
    'ai-generated';
  return {
    session_id: raw.ki_session_id,
    intent_pseudonym: raw.intent_pseudonym ?? '—',
    playbook_json: pj,
    playbook_yaml: playbookYaml,
    description_md:
      raw.description_md ?? pjDesc ?? `**KI-Vorschlag:** ${fallback.intent}`,
    proposed_slug: proposedSlug,
    proposed_display_name:
      raw.proposed_display_name ?? pjName ?? fallback.intent.slice(0, 80),
    // Default true wenn unbekannt: löst sicherheitshalber den Confirm-Dialog aus.
    is_destructive: raw.is_destructive ?? pjDestructive ?? true,
    requires_snapshot: raw.requires_snapshot ?? pjSnapshot ?? true,
    validation: mapValidation(raw.validation),
    usage: mapTokens(raw.tokens, raw.cost_usd),
    created_at: raw.created_at ?? new Date().toISOString(),
  };
}

function mapSessionItem(s: WireSessionItem): AiSession {
  const validation = {
    schema_valid: s.validation_passed !== false,
    allowlist_valid: s.validation_passed !== false,
    syntax_valid: s.validation_passed !== false,
  };
  let status: AiSession['status'] = 'success';
  if (s.validation_passed === false) status = 'failed';
  if (s.routine_id) status = 'accepted';
  return {
    id: s.id,
    created_at: s.started_at,
    customer_id: s.customer_id ?? null,
    system_id: s.system_id ?? null,
    // Backend liefert im Listen-Endpoint keinen Intent -> leer lassen, statt
    // still den technischen purpose als "Intent" auszugeben.
    intent_truncated: s.intent_truncated ?? '',
    purpose: s.purpose,
    status,
    validation_summary: validation,
    usage: {
      input_tokens: s.tokens_input ?? 0,
      cache_read_tokens: s.tokens_cache_read ?? 0,
      cache_creation_tokens: s.tokens_cache_creation ?? 0,
      output_tokens: s.tokens_output ?? 0,
      cost_usd: s.cost_usd ?? 0,
    },
    accepted_routine_id: s.routine_id ?? null,
  };
}

function mapSessionDetail(s: WireSessionDetail): AiSessionDetail {
  const base = mapSessionItem(s);
  const playbookYaml = extractYaml(s.pseudonymized_output);
  const intentFull = extractIntent(s.pseudonymized_input) ?? s.purpose;
  const validation: ValidationResult = {
    schema_valid: s.validation_passed !== false,
    allowlist_valid: s.validation_passed !== false,
    syntax_valid: s.validation_passed !== false,
    modules_used: [],
    modules_blocked: [],
    issues: extractIssues(s.validation_errors),
  };
  return {
    ...base,
    intent_full: intentFull,
    playbook_yaml: playbookYaml,
    description_md: '',
    validation,
  };
}

function extractYaml(obj: Record<string, unknown> | null | undefined): string {
  if (!obj) return '';
  const yaml = obj['playbook_yaml'];
  if (typeof yaml === 'string') return yaml;
  return jsonToYamlBestEffort(obj);
}

function extractIntent(obj: Record<string, unknown> | null | undefined): string | null {
  if (!obj) return null;
  const v = obj['intent'];
  return typeof v === 'string' ? v : JSON.stringify(obj, null, 2);
}

function extractIssues(
  obj: Record<string, unknown> | null | undefined
): ValidationIssue[] {
  if (!obj) return [];
  const out: ValidationIssue[] = [];
  const errors = obj['errors'];
  if (Array.isArray(errors)) {
    for (const e of errors) {
      if (typeof e === 'string') {
        out.push({ severity: 'error', check: 'schema', message: e });
      }
    }
  }
  const warnings = obj['warnings'];
  if (Array.isArray(warnings)) {
    for (const e of warnings) {
      if (typeof e === 'string') {
        out.push({ severity: 'warning', check: 'schema', message: e });
      }
    }
  }
  return out;
}

// Sehr einfache JSON->YAML-Heuristik, für den Fall dass Backend nur
// playbook_json schickt. Echtes pyyaml-Pendant würde 80kb kosten.
function jsonToYamlBestEffort(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return arrayToYaml(value, 0);
  if (typeof value === 'object') return objectToYaml(value as Record<string, unknown>, 0);
  return scalarToYaml(value);
}

function arrayToYaml(arr: unknown[], indent: number): string {
  const pad = ' '.repeat(indent);
  return arr
    .map((item) => {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        const inner = objectToYaml(item as Record<string, unknown>, indent + 2);
        const lines = inner.split('\n');
        const first = lines[0]?.trimStart() ?? '';
        const rest = lines.slice(1).join('\n');
        return `${pad}- ${first}${rest ? '\n' + rest : ''}`;
      }
      return `${pad}- ${scalarToYaml(item)}`;
    })
    .join('\n');
}

function objectToYaml(obj: Record<string, unknown>, indent: number): string {
  const pad = ' '.repeat(indent);
  return Object.entries(obj)
    .map(([k, v]) => {
      if (v === null || v === undefined) return `${pad}${k}: ~`;
      if (Array.isArray(v)) return `${pad}${k}:\n${arrayToYaml(v, indent + 2)}`;
      if (typeof v === 'object') {
        return `${pad}${k}:\n${objectToYaml(v as Record<string, unknown>, indent + 2)}`;
      }
      return `${pad}${k}: ${scalarToYaml(v)}`;
    })
    .join('\n');
}

function scalarToYaml(value: unknown): string {
  if (value === null || value === undefined) return '~';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return String(value);
  if (typeof value !== 'string') return JSON.stringify(value);
  if (/[:#"\n]/.test(value)) return JSON.stringify(value);
  return value;
}

// ---------------------------------------------------------------------------
// Query-Keys
// ---------------------------------------------------------------------------

export const aiKeys = {
  all: ['ai'] as const,
  sessions: (filter: AiSessionFilter) => ['ai', 'sessions', filter] as const,
  session: (id: string) => ['ai', 'session', id] as const,
  costStatus: ['ai', 'cost-status'] as const,
};

export interface AiSessionFilter {
  customerId?: string;
  status?: AiSession['status'];
  since?: string;
  limit?: number;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useAiSuggest(): ReturnType<
  typeof useMutation<AiSuggestResponse, Error, AiSuggestRequest>
> {
  const qc = useQueryClient();
  return useMutation<AiSuggestResponse, Error, AiSuggestRequest>({
    mutationFn: async (req) => {
      const body = {
        intent: req.intent,
        system_id: req.system_id,
        context: req.customer_id ? { customer_id: req.customer_id } : null,
      };
      // Kein DEV-Mock-Fallback mehr: ein 400 (z.B. fehlender ANTHROPIC_API_KEY)
      // muss sichtbar sein, statt durch eine Demo-Antwort verdeckt zu werden.
      const raw = await api.post<WireSuggestResponse, typeof body>(
        '/ai/suggest-routine',
        body
      );
      return mapSuggestResponse(raw, { intent: req.intent });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiKeys.costStatus });
    },
  });
}

export function useAiValidate(): ReturnType<
  typeof useMutation<AiValidateResponse, Error, AiValidateRequest>
> {
  return useMutation<AiValidateResponse, Error, AiValidateRequest>({
    mutationFn: async (req) => {
      // Backend AIValidatePlaybookRequest erwartet das strukturierte
      // playbook_json (PlaybookProposal, extra=forbid) - kein YAML-Wrapper.
      const body = { playbook_json: req.playbook_json };
      const raw = await api.post<WireValidateResponse, typeof body>(
        '/ai/validate-playbook',
        body
      );
      return { validation: mapValidation(raw.validation) };
    },
  });
}

export function useAiAccept(
  sessionId: string | null
): ReturnType<typeof useMutation<AiAcceptResponse, Error, AiAcceptRequest>> {
  const qc = useQueryClient();
  return useMutation<AiAcceptResponse, Error, AiAcceptRequest>({
    mutationFn: async (req) => {
      if (!sessionId) {
        throw new Error('Keine KI-Session aktiv.');
      }
      // Backend AIAcceptSuggestionRequest (extra=forbid): edited_playbook_json
      // ist das strukturierte, de-pseudonymisierte PlaybookProposal.
      // display_name/description/is_destructive leitet der Server aus dem
      // Proposal ab, deshalb senden wir sie NICHT mit. Kein DEV-Mock: ein
      // 400 (Validation fehlgeschlagen) muss den echten Fehler zeigen.
      const body = {
        edited_playbook_json: req.playbook_json,
        target_routine_code: req.target_routine_code ?? null,
      };
      const raw = await api.post<WireAcceptResponse, typeof body>(
        `/ai/suggest-routine/${sessionId}/accept`,
        body
      );
      const slug =
        raw.routine_slug ??
        raw.playbook_path?.split('/').pop()?.replace(/\.ya?ml$/, '') ??
        'ai-generated';
      return {
        routine_id: raw.routine_id,
        routine_slug: slug,
        redirect_to: `/routines/${slug}`,
      };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['routines'] });
      void qc.invalidateQueries({ queryKey: aiKeys.all });
    },
  });
}

export function useAiSessions(
  filter: AiSessionFilter = {}
): ReturnType<typeof useQuery<AiSessionListResponse>> {
  return useQuery<AiSessionListResponse>({
    queryKey: aiKeys.sessions(filter),
    queryFn: async () => {
      try {
        const raw = await api.get<WireSessionList>('/ai/sessions', {
          limit: filter.limit ?? 50,
        });
        return {
          data: raw.data.map(mapSessionItem),
          total: raw.total,
        };
      } catch (err) {
        if (err instanceof ApiError && err.status === 501 && import.meta.env.DEV) {
          return { data: [], total: 0 };
        }
        throw err;
      }
    },
    staleTime: 30_000,
  });
}

export function useAiSession(
  sessionId: string | undefined
): ReturnType<typeof useQuery<AiSessionDetail>> {
  return useQuery<AiSessionDetail>({
    queryKey: aiKeys.session(sessionId ?? ''),
    queryFn: async () => {
      const raw = await api.get<WireSessionDetail>(`/ai/sessions/${sessionId ?? ''}`);
      return mapSessionDetail(raw);
    },
    enabled: Boolean(sessionId),
  });
}

/**
 * Cost-Status: Backend hat (noch) keinen `/ai/cost-status` - wir aggregieren
 * client-seitig aus der Sessions-Liste. Sobald der Endpoint existiert, kann
 * dieser Hook umgestellt werden.
 */
export function useAiCostStatus(): ReturnType<typeof useQuery<AiCostStatus>> {
  return useQuery<AiCostStatus>({
    queryKey: aiKeys.costStatus,
    queryFn: async () => {
      try {
        const raw = await api.get<WireSessionList>('/ai/sessions', { limit: 200 });
        const today = new Date().toISOString().slice(0, 10);
        const monthPrefix = today.slice(0, 7);
        let costToday = 0;
        let costMonth = 0;
        for (const s of raw.data) {
          const day = s.started_at.slice(0, 10);
          const cost = Number(s.cost_usd ?? 0);
          if (day === today) costToday += cost;
          if (day.startsWith(monthPrefix)) costMonth += cost;
        }
        // HARTKODIERT (Client-seitig): Das Backend hat (noch) keinen
        // /ai/cost-status-Endpoint, der die konfigurierten Limits liefert.
        // Sobald es ihn gibt, sollten diese Werte von dort kommen.
        const SOFT = 1.0;
        const HARD = 5.0;
        return {
          date: today,
          cost_today_usd: costToday,
          cost_this_month_usd: costMonth,
          daily_soft_limit_usd: SOFT,
          daily_hard_limit_usd: HARD,
          soft_limit_reached: costToday > SOFT,
          hard_limit_reached: costToday >= HARD,
        };
      } catch {
        // Falls Sessions-Endpoint nicht da ist (z.B. 501), kein Banner.
        return mockCostStatusZero();
      }
    },
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Run-Diff (WT-504 / Sprint 6 Mount)
// ---------------------------------------------------------------------------

import type {
  RunDiffResponse,
  SystemDiff,
  DiffSummaryEntry,
  RoutineResultEntry,
} from '@/hooks/useRuns';

interface WireRunDiff {
  run_id: string;
  changed_packages: Array<Record<string, unknown>>;
  changed_files: Array<Record<string, unknown>>;
  changed_services: Array<Record<string, unknown>>;
  other?: Array<Record<string, unknown>>;
  summary?: Record<string, number>;
}

/**
 * diff.other tolerant in einen RoutineResultEntry gießen. Die Einträge
 * schreibt der wt-client (baue_routine_diff); kaputte Formen dürfen die
 * Anzeige nicht kosten - dann steht der Rohinhalt eben unter result.
 */
function mapOtherEntry(raw: Record<string, unknown>): RoutineResultEntry {
  const result = raw['result'];
  return {
    routine: typeof raw['routine'] === 'string' ? raw['routine'] : null,
    host: typeof raw['host'] === 'string' ? raw['host'] : null,
    result:
      typeof result === 'object' && result !== null && !Array.isArray(result)
        ? (result as Record<string, unknown>)
        : raw,
  };
}

function mapDiffEntry(
  raw: Record<string, unknown>,
  kind: string
): DiffSummaryEntry {
  const targetCandidate = raw['target'] ?? raw['name'] ?? raw['path'];
  const target =
    typeof targetCandidate === 'string'
      ? targetCandidate
      : typeof targetCandidate === 'number'
        ? String(targetCandidate)
        : '';
  return {
    kind,
    target,
    before: typeof raw['before'] === 'string' ? raw['before'] : null,
    after: typeof raw['after'] === 'string' ? raw['after'] : null,
    action: typeof raw['action'] === 'string' ? raw['action'] : null,
  };
}

export function useRunDiff(
  runId: string | undefined,
  options: { enabled?: boolean } = {}
): ReturnType<typeof useQuery<RunDiffResponse>> {
  const enabled = Boolean(runId) && options.enabled !== false;
  return useQuery<RunDiffResponse>({
    queryKey: ['runs', 'detail', runId ?? '', 'diff'],
    queryFn: async () => {
      try {
        const raw = await api.get<WireRunDiff>(`/runs/${runId ?? ''}/diff`);
        // `other` zählt MIT: Client-Runs liefern ihren geernteten
        // Result-Block ausschließlich dort, und ein reiner other-Diff
        // zeigte vorher "Kein Diff verfuegbar" ohne Tab-Badge (Befund
        // Wartungsnächte 19./20.08.2026).
        const total =
          (raw.changed_files?.length ?? 0) +
          (raw.changed_packages?.length ?? 0) +
          (raw.changed_services?.length ?? 0) +
          (raw.other?.length ?? 0);
        // Backend liefert flach (alle Steps aggregiert). Wir packen das in
        // ein Pseudo-System "alle Targets" damit DiffSummaryCard wiederverwendet
        // werden kann.
        const systems: SystemDiff[] =
          total === 0
            ? []
            : [
                {
                  system_id: raw.run_id,
                  system_hostname: 'Aggregat (alle Hosts)',
                  customer_name: null,
                  changed_files: (raw.changed_files ?? []).map((e) =>
                    mapDiffEntry(e, 'file')
                  ),
                  changed_packages: (raw.changed_packages ?? []).map((e) =>
                    mapDiffEntry(e, 'package')
                  ),
                  changed_services: (raw.changed_services ?? []).map((e) =>
                    mapDiffEntry(e, 'service')
                  ),
                  other: (raw.other ?? []).map(mapOtherEntry),
                  notes: null,
                },
              ];
        return {
          run_id: raw.run_id,
          generated_at: new Date().toISOString(),
          systems,
          truncated: false,
        };
      } catch (err) {
        if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
          return {
            run_id: runId ?? '',
            generated_at: new Date().toISOString(),
            systems: [],
            truncated: false,
          };
        }
        throw err;
      }
    },
    enabled,
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ALLOWED_MODULES = new Set([
  'apt',
  'dnf',
  'yum',
  'package',
  'service',
  'systemd',
  'copy',
  'template',
  'lineinfile',
  'cron',
  'file',
  'debug',
  'assert',
  'wait_for',
  'community.general.proxmox',
  'community.general.proxmox_kvm',
  'community.general.proxmox_snap',
  'ansible.builtin.apt',
  'ansible.builtin.service',
  'ansible.builtin.systemd',
  'ansible.builtin.copy',
  'ansible.builtin.template',
  'ansible.builtin.lineinfile',
  'ansible.builtin.cron',
  'ansible.builtin.file',
  'ansible.builtin.debug',
  'ansible.builtin.assert',
  'ansible.builtin.package',
]);

const FORBIDDEN_MODULES = new Set([
  'shell',
  'raw',
  'command',
  'script',
  'ansible.builtin.shell',
  'ansible.builtin.raw',
  'ansible.builtin.command',
]);

export function detectModulesInYaml(yaml: string): {
  modules: string[];
  blocked: string[];
} {
  const lines = yaml.split('\n');
  const modules = new Set<string>();
  const blocked = new Set<string>();

  for (const line of lines) {
    const m = line.match(/^\s{2,}([a-zA-Z][a-zA-Z0-9_.]*)\s*:/);
    if (!m || !m[1]) continue;
    const name = m[1];
    if (RESERVED_KEYS.has(name)) continue;
    if (FORBIDDEN_MODULES.has(name)) {
      blocked.add(name);
      continue;
    }
    if (
      ALLOWED_MODULES.has(name) ||
      name.startsWith('community.') ||
      name.startsWith('ansible.')
    ) {
      modules.add(name);
    } else if (looksLikeAnsibleModule(name)) {
      blocked.add(name);
    }
  }

  return { modules: Array.from(modules), blocked: Array.from(blocked) };
}

const RESERVED_KEYS = new Set([
  'name',
  'hosts',
  'become',
  'become_user',
  'vars',
  'tasks',
  'pre_tasks',
  'post_tasks',
  'handlers',
  'roles',
  'tags',
  'when',
  'register',
  'notify',
  'block',
  'rescue',
  'always',
  'loop',
  'with_items',
  'delegate_to',
  'run_once',
  'changed_when',
  'failed_when',
  'check_mode',
  'diff',
  'gather_facts',
  'no_log',
  'state',
  'mode',
  'owner',
  'group',
  'src',
  'dest',
  'path',
  'line',
  'regexp',
  'value',
  'key',
  'enabled',
  'started',
  'restarted',
  'minute',
  'hour',
  'day',
  'month',
  'weekday',
  'job',
  'user',
  'special_time',
  'msg',
  'that',
  'fail_msg',
  'success_msg',
  'update_cache',
  'cache_valid_time',
  'force_apt_get',
  'autoremove',
  'autoclean',
  'install_recommends',
]);

function looksLikeAnsibleModule(name: string): boolean {
  if (name.includes('.')) return true;
  return /^[a-z][a-z0-9_]*$/.test(name) && name.length > 2;
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);
}

function guessSlugFromYaml(yaml: string): string | null {
  const m = yaml.match(/^\s*-?\s*name:\s*"?([^"\n]+)"?/m);
  if (!m || !m[1]) return null;
  return slugify(m[1]);
}

// ---------------------------------------------------------------------------
// Fallback-Defaults
// ---------------------------------------------------------------------------
// Hinweis: Die früheren DEV-Mocks für suggest/validate/accept wurden
// entfernt - sie kaschierten echte 4xx-Fehler (z.B. fehlender
// ANTHROPIC_API_KEY oder Schema-Verletzungen). Der Cost-Status hat weiterhin
// einen neutralen Default, weil es (noch) keinen /ai/cost-status-Endpoint gibt.

function mockCostStatusZero(): AiCostStatus {
  return {
    date: new Date().toISOString().slice(0, 10),
    cost_today_usd: 0,
    cost_this_month_usd: 0,
    daily_soft_limit_usd: 1.0,
    daily_hard_limit_usd: 5.0,
    soft_limit_reached: false,
    hard_limit_reached: false,
  };
}
